import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Truck, Calendar, FileText, X, Pencil, Trash2, Check, Undo2, AlertTriangle } from 'lucide-react';
// Shared with ZenBookings so both booking forms handle money identically.
import { money, stripThousands, formatThousands, handleMoneyInput } from '../utils/money';
// Shared with ZenBookings, whose table now uses the same paired-cell language.
import { formatDateShort, formatAmount } from '../utils/bookingFormat';
import DepositPaymentsForm, { buildDepositFormData } from './DepositPaymentsForm';
import DepositReceiptReview from './DepositReceiptReview';

const API_BASE = '/api';

// The six house venues, worded and ordered exactly as constants/meetingOptions VENUES.
// A booking whose venue is not one of these shows as "Other (specify)" on edit.
const VENUES = [
  'W Factory',
  'W Nexus (1 Utama)',
  'W Timber',
  'W in the Sky',
  'W Nexus (Kelana Jaya)',
  'W Nexus (Kuala Lumpur)'
];
const VENUE_OTHER = 'Other (specify)';

const EVENT_TYPES = [
  'Wedding',
  'Corporate',
  'Private'
]; // match the scorecard Done-Deals columns

function JQBookings({ month, onBookingChange }) {
  const { token, isSuperadmin, user } = useAuth();
  // The booking mutations (create/edit/delete) are the JQ salesperson's own —
  // backend requires requireJQ (rejects admin AND other salespeople). Gate the
  // controls on an explicit JQ check, not merely "not admin", so a different
  // salesperson who reaches this page never sees a control that only 403s.
  // Gate on the linked salesperson (stable), not the editable display name —
  // e.g. login "JQ Chew" whose salesperson is "JQ". Fall back to name for old tokens.
  const isJQ =
    (user?.salesperson_name || '').trim().toLowerCase() === 'jq' ||
    (user?.name || '').trim().toLowerCase() === 'jq';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [deliverModal, setDeliverModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [paymentSeed, setPaymentSeed] = useState(null);
  const [paymentDraft, setPaymentDraft] = useState(null);
  const [paymentFormKey, setPaymentFormKey] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editImportedDetails, setEditImportedDetails] = useState(false);
  const handleDepositChange = useCallback(value => setPaymentDraft(value), []);

  // New/edit booking form state
  const [form, setForm] = useState({
    transfer_date: '',
    event_date: '',
    event_type: '',
    venue: '',
    venue_other: '',
    package: '',
    client_name: '',
    invoice_number: '',
    forecast_pax: '',
    forecast_contract_value: '',
    deposit_amount: ''
  });

  // Delivery form state
  const [actualValue, setActualValue] = useState('');

  // Tier rate info
  const [tierInfo, setTierInfo] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchTierRate();
  }, [month]);

  const fetchTierRate = async () => {
    try {
      const response = await axios.get(`${API_BASE}/jq-bookings/tier-rate?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTierInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch tier rate:', err);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/jq-bookings?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ transfer_date: '', event_date: '', event_type: '', venue: '', venue_other: '', package: '', client_name: '', invoice_number: '', forecast_pax: '', forecast_contract_value: '', deposit_amount: '' });
    setEditingBooking(null);
    setShowForm(false);
    setPaymentSeed(null);
    setPaymentDraft(null);
    setEditImportedDetails(false);
    setPaymentFormKey(key => key + 1);
  };

  const startEdit = async (booking) => {
    setPaymentSeed(null);
    setForm({
      transfer_date: booking.transfer_date ? booking.transfer_date.substring(0, 10) : '',
      event_date: booking.event_date ? booking.event_date.substring(0, 10) : '',
      event_type: booking.event_type,
      venue: VENUES.includes(booking.venue) ? booking.venue : VENUE_OTHER,
      venue_other: VENUES.includes(booking.venue) ? '' : (booking.venue || ''),
      package: booking.package || '',
      client_name: booking.client_name || '',
      invoice_number: booking.invoice_number || '',
      forecast_pax: booking.forecast_pax != null ? booking.forecast_pax : '',
      // The API returns plain decimal strings; group them so an existing booking reads
      // the same way as one being typed.
      forecast_contract_value: formatThousands(booking.forecast_contract_value),
      // Bookings created before the deposit column exists come back null — leave the
      // box empty so JQ has to type the real figure rather than confirm a fake zero.
      deposit_amount: booking.deposit_amount != null ? formatThousands(booking.deposit_amount) : ''
    });
    setEditingBooking(booking);
    setShowForm(true);
    setEditImportedDetails(false);
    setPaymentDraft(null);
    if (booking.receipt_required) {
      setPaymentLoading(true);
      try {
        const response = await axios.get(`${API_BASE}/jq-bookings/${booking.id}/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPaymentSeed(response.data);
        setPaymentFormKey(key => key + 1);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load deposit receipts');
      } finally { setPaymentLoading(false); }
    }
  };

  // Regroup as the user types, then put the caret back where it was relative to the
  // digits — without this, inserting a separator throws the cursor to the end and
  // makes editing the middle of a figure impossible.
  const handleMoneyChange = (field) => (e) =>
    handleMoneyInput(e, (next) => setForm((prev) => ({ ...prev, [field]: next })));

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const future = !editingBooking || editingBooking.receipt_required;
    const attachmentOnly = editingBooking?.deposit_entry_source === 'spreadsheet' && !editImportedDetails;
    if (future && (!paymentDraft?.valid || paymentLoading)) {
      setError('Enter the required deposit and attach a receipt for every payment.');
      setSubmitting(false);
      return;
    }
    if (attachmentOnly) {
      try {
        await axios.put(`${API_BASE}/jq-bookings/${editingBooking.id}/payments`,
          buildDepositFormData({}, paymentDraft), { headers: { Authorization: `Bearer ${token}` } });
        setSuccess('Deposit receipts attached');
        resetForm();
        fetchBookings();
      } catch (err) { setError(err.response?.data?.error || 'Failed to attach receipts'); }
      finally { setSubmitting(false); }
      return;
    }

    // "Other (specify)" → use the typed venue name as the booking's venue.
    const venue = form.venue === VENUE_OTHER ? (form.venue_other || '').trim() : form.venue;
    if (form.venue === VENUE_OTHER && !venue) { setError('Please specify the venue'); setSubmitting(false); return; }
    // The money fields carry thousand separators for readability — strip them before
    // any comparison or the request, since parseFloat("250,000") is 250.
    const depositAmount = stripThousands(form.deposit_amount);
    const contractValue = stripThousands(form.forecast_contract_value);

    // `required` alone would let a typed 0 through — the deposit must be real money.
    if (!future && !(parseFloat(depositAmount) > 0)) {
      setError('Deposit amount must be greater than 0');
      setSubmitting(false);
      return;
    }
    // Native number validation went away with type="number"; keep the guard here so a
    // stray value is caught before the round-trip.
    if (!(parseFloat(contractValue) >= 0)) {
      setError('Forecast contract value must be a valid number');
      setSubmitting(false);
      return;
    }

    const payload = {
      ...form,
      venue,
      deposit_amount: depositAmount,
      forecast_contract_value: contractValue
    };

    try {
      const requestBody = future ? buildDepositFormData(payload, paymentDraft) : payload;
      let response;
      if (editingBooking) {
        response = await axios.put(`${API_BASE}/jq-bookings/${editingBooking.id}`, requestBody, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API_BASE}/jq-bookings`, requestBody, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSuccess(response.data.message);
      resetForm();
      fetchBookings();
      fetchTierRate();
      if (onBookingChange) onBookingChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Delete this booking?')) return;
    setError(null);
    try {
      await axios.delete(`${API_BASE}/jq-bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Booking deleted');
      fetchBookings();
      fetchTierRate();
      if (onBookingChange) onBookingChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete booking');
    }
  };

  const handleVerifyAll = async () => {
    const n = bookings.filter(b => !b.verified).length;
    if (!n) return;
    if (!window.confirm(`Verify all ${n} unverified booking${n === 1 ? '' : 's'} for this month? Their 70% will then count toward commission.`)) return;
    setError(null);
    try {
      const res = await axios.put(`${API_BASE}/jq-bookings/verify-all`, { month }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(res.data.message);
      fetchBookings();
      if (onBookingChange) onBookingChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify all');
    }
  };

  const handleVerify = async (bookingId, verified) => {
    setError(null);
    try {
      await axios.put(`${API_BASE}/jq-bookings/${bookingId}/verify`, { verified }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(verified ? 'Booking verified' : 'Booking un-verified');
      fetchBookings();
      if (onBookingChange) onBookingChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify booking');
    }
  };

  const handleDeliver = async (bookingId) => {
    if (!actualValue) {
      setError('Please enter the actual final value');
      return;
    }
    // Strip the display separators — parseFloat("250,000") is 250, and this figure
    // is what the final commission and the 30% settlement are computed from.
    const actualNum = parseFloat(stripThousands(actualValue));
    if (!Number.isFinite(actualNum) || actualNum < 0) {
      setError('Actual final value must be a valid number');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.put(`${API_BASE}/jq-bookings/${bookingId}/deliver`, {
        actual_final_value: actualNum
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(response.data.message);
      setDeliverModal(null);
      setActualValue('');
      fetchBookings();
      if (onBookingChange) onBookingChange();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark as delivered');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => money(parseFloat(amount) || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const unverifiedCount = bookings.filter(b => !b.verified).length;

  // Attribute commission to the correct month, matching the backend aggregate():
  //  - 70% capture counts in the BOOKING month
  //  - 30% settlement counts in the DELIVERED (event) month, paid after the event
  // The API returns rows where booking_month === month OR delivered_month === month,
  // so a deal booked in May but delivered in June appears on BOTH months' lists.
  // Each card/table must filter to the month it represents, or a June delivery's 30%
  // leaks into May's settlements (and a June booking's 70% leaks the other way).
  const bookedThisMonth = bookings.filter(b => b.booking_month === month);
  const pendingBookings = bookedThisMonth.filter(b => b.delivery_status === 'pending');
  const deliveredBookings = bookings.filter(b => b.delivery_status === 'delivered' && b.delivered_month === month);

  // 70% only counts toward commission once a booking's deposit is admin-verified.
  // Show the verified total prominently and the unverified amount as "pending verification".
  // Captured is what an admin is left with once the commission figures are hidden:
  // how much was booked this month, on the same basis as Zen's card (every booking
  // whose capture month is this month, verified or not).
  const capturedTotal = bookedThisMonth.reduce((sum, b) => sum + parseFloat(b.forecast_contract_value || 0), 0);

  const verifiedPaid70 = bookedThisMonth.filter(b => b.verified).reduce((sum, b) => sum + parseFloat(b.paid_70_percent || 0), 0);
  const unverifiedPaid70 = bookedThisMonth.filter(b => !b.verified).reduce((sum, b) => sum + parseFloat(b.paid_70_percent || 0), 0);
  const totalSettlements = deliveredBookings.reduce((sum, b) => sum + parseFloat(b.settlement_amount || 0), 0);
  const totalCommission = verifiedPaid70 + totalSettlements;

  return (
    <div className="jq-bookings bookings-view">
      <div className="jq-header">
        <h3>JQ Booking Tracker <span className="jq-header-month">· {month}</span></h3>
        {/* Booking creation is JQ-only; admin's JQ Bookings view is read-only (Verify excepted). */}
        {isJQ && (
          <button className="btn-primary" onClick={() => { if (showForm) { resetForm(); } else { setShowForm(true); } }}>
            {showForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> New Booking</>}
          </button>
        )}
        {isSuperadmin && unverifiedCount > 0 && (
          <button className="btn-primary" onClick={handleVerifyAll} title="Verify every unverified booking this month">
            <Check size={15} /> Verify all ({unverifiedCount})
          </button>
        )}
      </div>

      {/* Current Tier Rate Info */}
      {/* Everything below that carries commission — the tier rate, the 70/30 split,
          the settlement and the final figure — is JQ's own pay, and is shown only to
          JQ. An admin comes to this page to verify deposits and release delivery, and
          the commission totals live on the Commissions page. */}
      {isJQ && tierInfo && (
        <div className="tier-rate-banner">
          <span>Cumulative Booked Value: <strong>{formatCurrency(tierInfo.cumulativeRevenue)}</strong></span>
          <span className="tier-rate-divider">|</span>
          <span>Current Tier Rate: <strong>{tierInfo.tierRate.toFixed(2)}%</strong></span>
          {tierInfo.tierRate === 0 && <span className="tier-rate-note">(Below RM 200k threshold)</span>}
        </div>
      )}

      {/* Success / Error messages */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error} <button className="alert-close" onClick={() => setError(null)}>&times;</button></div>}

      {/* Where the commission cards sit for JQ, an admin gets the one figure that is
          theirs to care about — the same CAPTURED card Zen's page carries. */}
      {!isJQ && (
        <div className="jq-summary-card captured-card">
          <p className="jq-summary-label">Captured</p>
          <h4>{formatCurrency(capturedTotal)}</h4>
          <p className="jq-summary-count">{bookedThisMonth.length} booking{bookedThisMonth.length === 1 ? '' : 's'}</p>
        </div>
      )}

      {/* Commission Summary Cards */}
      {isJQ && <div className="jq-summary-grid">
        <div className="jq-summary-card">
          <p className="jq-summary-label">70% Paid (Verified)</p>
          <h4>{formatCurrency(verifiedPaid70)}</h4>
          <p className="jq-summary-count">
            {bookedThisMonth.length} booking{bookedThisMonth.length !== 1 ? 's' : ''}
            {unverifiedPaid70 > 0 && <> · <span style={{ color: 'var(--rmc-clay)' }}>{formatCurrency(unverifiedPaid70)} pending verification</span></>}
          </p>
        </div>
        <div className="jq-summary-card">
          <p className="jq-summary-label">30% Settlements</p>
          <h4>{formatCurrency(totalSettlements)}</h4>
          <p className="jq-summary-count">{deliveredBookings.length} delivered</p>
        </div>
        <div className="jq-summary-card highlight">
          <p className="jq-summary-label">Total Commission</p>
          <h4>{formatCurrency(totalCommission)}</h4>
          <p className="jq-summary-count">{pendingBookings.length} pending delivery</p>
        </div>
      </div>}

      {/* New Booking Form */}
      {showForm && (
        <div className="booking-form-card">
          <h4><FileText size={18} /> {editingBooking ? 'Edit Booking' : 'Create New Booking (70% Payment)'}</h4>
          <form onSubmit={handleSubmitBooking}>
            {/* Money-in, held in its own panel: when the cash landed (which sets the
                capture month) and how much of it. Nothing here feeds the commission
                maths — the rate and the 70% come off the forecast contract value. */}
            {(!editingBooking || editingBooking.receipt_required) ? (
              paymentLoading ? <p>Loading deposit receipts...</p> :
                <DepositPaymentsForm key={paymentFormKey}
                  initialRequired={paymentSeed?.required_deposit_amount || ''}
                  initialPayments={paymentSeed?.payments || []}
                  onChange={handleDepositChange} />
            ) : <div className="deposit-panel">
              <span className="deposit-panel-label">Deposit</span>
              <span className="field-hint">Receipt not required (older booking)</span>
              <div className="form-grid">
                <div className="form-group">
                  <label>Received Date *</label>
                  <input
                    type="date"
                    className="w-date"
                    value={form.transfer_date}
                    onChange={(e) => setForm({ ...form, transfer_date: e.target.value })}
                    required
                  />
                  <span className="field-hint">sets capture month</span>
                </div>
                <div className="form-group">
                  <label>Amount (RM) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-money"
                    value={form.deposit_amount}
                    onChange={handleMoneyChange('deposit_amount')}
                    placeholder="20,000.00"
                    required
                  />
                </div>
              </div>
            </div>}

            {editingBooking?.deposit_entry_source === 'spreadsheet' && <div className="field-hint">
              {editImportedDetails ? 'Editing booking details and deposit transactions.' : 'Attaching receipts to an imported booking. Its saved amount and date stay the same.'}
              <button type="button" className="btn-secondary" onClick={() => setEditImportedDetails(value => !value)}>
                {editImportedDetails ? 'Attach receipts only' : 'Edit booking details instead'}
              </button>
            </div>}

            <fieldset disabled={editingBooking?.deposit_entry_source === 'spreadsheet' && !editImportedDetails} style={{ border: 0, padding: 0, margin: 0 }}>
            <div className="form-grid">
              <div className="form-group">
                <label>Event Date *</label>
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Venue *</label>
                <select
                  value={form.venue}
                  onChange={(e) => setForm({ ...form, venue: e.target.value })}
                  required
                >
                  <option value="">Select venue...</option>
                  {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
                  <option value={VENUE_OTHER}>{VENUE_OTHER}</option>
                </select>
                {form.venue === VENUE_OTHER && (
                  <input
                    type="text"
                    placeholder="Specify venue"
                    value={form.venue_other}
                    onChange={(e) => setForm({ ...form, venue_other: e.target.value })}
                    style={{ marginTop: 6 }}
                  />
                )}
              </div>
              <div className="form-group">
                <label>Event Type *</label>
                <select
                  value={form.event_type}
                  onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                  required
                >
                  <option value="">Select type...</option>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Pax (Forecast)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.forecast_pax}
                  onChange={(e) => setForm({ ...form, forecast_pax: e.target.value })}
                  placeholder="e.g. 300"
                />
              </div>
              <div className="form-group">
                <label>Client Name</label>
                <input
                  type="text"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="e.g. Chong Wai Hong"
                />
              </div>
              <div className="form-group">
                <label>Invoice Number</label>
                <input
                  type="text"
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="e.g. IV2706-00001"
                />
                {/* Optional on purpose — the number is issued after the deposit lands.
                    But revenue is counted off the invoices synced from Bukku, not off
                    this field, so a booking with no invoice raised never counts. */}
                <span className="field-warn"><AlertTriangle size={13} /> Without an invoice raised, this sale will not be counted</span>
              </div>
              <div className="form-group">
                <label>Package</label>
                <input
                  type="text"
                  value={form.package}
                  onChange={(e) => setForm({ ...form, package: e.target.value })}
                  placeholder="e.g. Platinum Wedding"
                />
              </div>
              <div className="form-group full-width">
                <label>Forecast Contract Value (RM) *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.forecast_contract_value}
                  onChange={handleMoneyChange('forecast_contract_value')}
                  placeholder="e.g. 250,000.00"
                  required
                />
              </div>
            </div>
            </fieldset>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : editingBooking?.deposit_entry_source === 'spreadsheet' && !editImportedDetails ? 'Attach receipts' : editingBooking ? 'Save Changes' : 'Create Booking'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pending Bookings */}
      {pendingBookings.length > 0 && (
        <div className="bookings-section">
          <h4><Calendar size={18} /> Pending Delivery ({pendingBookings.length})</h4>
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                {/* Paired fields share a cell — the subordinate value sits small and
                    muted beneath the primary one. Fourteen columns become nine, which
                    is what lets the package name show without wrapping the row. */}
                <tr>
                  <th className="col-num cell-dep">Deposit<span className="th-sub">received</span></th>
                  <th>Client<span className="th-sub">invoice #</span></th>
                  <th>Event<span className="th-sub">venue</span></th>
                  <th>Package<span className="th-sub">type</span></th>
                  <th className="col-num">Pax</th>
                  <th className="col-num">Contract RM{isJQ && <span className="th-sub">rate</span>}</th>
                  {isJQ && <th className="col-num">70% RM</th>}
                  <th className="cell-status" title="Verification status"><span className="sr-only">Status</span></th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingBookings.map(booking => (
                  <tr key={booking.id}>
                    <td className="col-num cell-dep">
                      <span className="cell-main">{formatAmount(booking.deposit_amount)}</span>
                      <span className="cell-sub">{formatDateShort(booking.transfer_date)}</span>
                      <DepositReceiptReview source="jq" bookingId={booking.id} token={token} receiptRequired={booking.receipt_required} />
                    </td>
                    <td>
                      <span className="cell-main cell-clip" title={booking.client_name || ''}>{booking.client_name || '—'}</span>
                      <span className="cell-sub">{booking.invoice_number || '—'}</span>
                    </td>
                    <td>
                      <span className="cell-main">{formatDateShort(booking.event_date)}</span>
                      {/* Venue names run long ("Edenmills by Metalbees") — clip rather
                          than let one row set the column's width. */}
                      <span className="cell-sub cell-clip" title={booking.venue || ''}>{booking.venue}</span>
                    </td>
                    <td>
                      <span className="cell-main cell-clip cell-clip-wide" title={booking.package || ''}>{booking.package || '—'}</span>
                      <span className="cell-sub">{booking.event_type}</span>
                    </td>
                    <td className="col-num">{booking.forecast_pax != null ? booking.forecast_pax : '—'}</td>
                    <td className="col-num">
                      <span className="cell-main">{formatAmount(booking.forecast_contract_value)}</span>
                      {isJQ && <span className="cell-sub">{parseFloat(booking.forecast_tier_rate).toFixed(2)}%</span>}
                    </td>
                    {isJQ && <td className="col-num amount-positive">{formatAmount(booking.paid_70_percent)}</td>}
                    {/* Status as a dot, not a word. "Pending verify" spelled out cost
                        more width than the information was worth, and the action
                        button beside it already says which state the row is in. */}
                    <td className="cell-status">
                      <span
                        className={`status-dot ${booking.verified ? 'is-verified' : 'is-pending'}`}
                        title={booking.verified ? 'Verified' : 'Pending verification'}
                      />
                    </td>
                    <td>
                      {/* Icon-only, so the column costs ~70px instead of ~200px. Every
                          button keeps a title, and aria-label carries the same words
                          for anyone not using a pointer. */}
                      <div className="action-buttons">
                        {isSuperadmin && !booking.verified && (
                          <button className="btn-icon btn-verify" onClick={() => handleVerify(booking.id, true)} title="Verify deposit" aria-label="Verify deposit">
                            <Check size={15} />
                          </button>
                        )}
                        {isSuperadmin && booking.verified && (
                          <>
                            <button className="btn-icon" onClick={() => handleVerify(booking.id, false)} title="Un-verify" aria-label="Un-verify">
                              <Undo2 size={15} />
                            </button>
                            {/* Releasing the 30% (delivery) is an admin-only control step. */}
                            <button
                              className="btn-icon btn-verify"
                              onClick={() => { setDeliverModal(booking); setActualValue(formatThousands(booking.forecast_contract_value)); }}
                              title="Release 30% (mark delivered)"
                              aria-label="Mark delivered"
                            >
                              <Truck size={15} />
                            </button>
                          </>
                        )}
                        {isJQ && (
                          <>
                            <button className="btn-icon" onClick={() => startEdit(booking)} title="Edit" aria-label="Edit booking">
                              <Pencil size={15} />
                            </button>
                            <button className="btn-icon btn-danger" onClick={() => handleDelete(booking.id)} title="Delete" aria-label="Delete booking">
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delivered Bookings */}
      {deliveredBookings.length > 0 && (
        <div className="bookings-section">
          <h4><Truck size={18} /> Delivered ({deliveredBookings.length})</h4>
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Event Date</th>
                  <th>Client</th>
                  <th>Invoice #</th>
                  <th>Venue</th>
                  <th>Package</th>
                  <th>Deposit</th>
                  <th>Contract Value</th>
                  <th>Actual Value</th>
                  {isJQ && <th>Rate (Locked)</th>}
                  {isJQ && <th>70% Paid</th>}
                  {isJQ && <th>30% Settlement</th>}
                  {isJQ && <th>Total Commission</th>}
                </tr>
              </thead>
              <tbody>
                {deliveredBookings.map(booking => (
                  <tr key={booking.id}>
                    <td>{formatDate(booking.event_date)}</td>
                    <td>{booking.client_name || '—'}</td>
                    <td>{booking.invoice_number || '—'}</td>
                    <td>{booking.venue}</td>
                    <td className="col-package">{booking.package || '—'}</td>
                    <td className="col-num">{booking.deposit_amount != null ? formatCurrency(booking.deposit_amount) : '—'}</td>
                    <td className="col-num">{formatCurrency(booking.forecast_contract_value)}</td>
                    <td className="col-num">{formatCurrency(booking.actual_final_value)}</td>
                    {isJQ && <td className="col-num">{parseFloat(booking.forecast_tier_rate).toFixed(2)}%</td>}
                    {isJQ && <td className="col-num">{formatCurrency(booking.paid_70_percent)}</td>}
                    {isJQ && <td className={parseFloat(booking.settlement_amount) >= 0 ? 'amount-positive' : 'amount-negative'}>
                      {formatCurrency(booking.settlement_amount)}
                    </td>}
                    {isJQ && <td className="col-num"><strong>{formatCurrency(booking.final_commission)}</strong></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && bookings.length === 0 && !showForm && (
        <div className="empty-state">
          <FileText size={48} />
          <p>No bookings for this month yet.</p>
          {isJQ && (
            <button className="btn-primary" onClick={() => setShowForm(true)}>
              <Plus size={16} /> Create First Booking
            </button>
          )}
        </div>
      )}

      {/* Delivery Modal */}
      {deliverModal && (
        <div className="modal-overlay" onClick={() => setDeliverModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Mark as Delivered</h4>
            <p className="modal-subtitle">
              Venue: <strong>{deliverModal.venue}</strong> |
              Locked Rate: <strong>{parseFloat(deliverModal.forecast_tier_rate).toFixed(2)}%</strong>
            </p>

            <div className="modal-details">
              <div className="modal-row">
                <span>Forecast Contract Value:</span>
                <span>{formatCurrency(deliverModal.forecast_contract_value)}</span>
              </div>
              <div className="modal-row">
                <span>70% Already Paid:</span>
                <span>{formatCurrency(deliverModal.paid_70_percent)}</span>
              </div>
            </div>

            <div className="form-group">
              <label>Actual Final Invoice Value (RM) *</label>
              <input
                type="text"
                inputMode="decimal"
                value={actualValue}
                onChange={(e) => handleMoneyInput(e, setActualValue)}
                placeholder="Enter actual final value"
                autoFocus
              />
            </div>

            {/* Derive the number once. Every figure below is computed from it, and a
                stray comma would silently turn 250,000 into 250 across all of them. */}
            {actualValue && (() => {
              const actualNum = parseFloat(stripThousands(actualValue));
              const lockedRate = parseFloat(deliverModal.forecast_tier_rate);
              const finalCommission = actualNum * lockedRate / 100;
              const settlement = finalCommission - parseFloat(deliverModal.paid_70_percent);
              if (!Number.isFinite(actualNum)) return null;
              return (
                <div className="modal-preview">
                  <div className="modal-row">
                    <span>Final Commission ({lockedRate.toFixed(2)}% of {formatCurrency(actualNum)}):</span>
                    <span>{formatCurrency(finalCommission)}</span>
                  </div>
                  <div className="modal-row highlight">
                    <span>30% Settlement Due:</span>
                    <span className={settlement >= 0 ? 'amount-positive' : 'amount-negative'}>
                      {formatCurrency(settlement)}
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="modal-actions">
              <button className="btn-primary" onClick={() => handleDeliver(deliverModal.id)} disabled={submitting}>
                {submitting ? 'Processing...' : 'Confirm Delivery & Settle 30%'}
              </button>
              <button className="btn-secondary" onClick={() => setDeliverModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JQBookings;
